import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import useUniqueId from './useUniqueId';
import FormLabel from './FormLabel';
import styles from './MultiSelectDropdown.module.css';

// Custom event type for multi-select
export interface MultiSelectChangeEvent {
    target: {
        name: string;
        value: (string | number)[];
    };
}

/**
 * A multi-select dropdown allows users to select multiple options from a dropdown menu.
 * Also renders supporting UI elements like a `label`, hint text, and error message.
 * Maintains the same look and feel as the standard Dropdown component.
 */
function MultiSelectDropdown(props: MultiSelectDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Determine if there's an error based on whether the field is submitted and has no value
    const hasError =
        (props.submitted && (!props.value || props.value.length === 0)) ||
        !!props.errorMsg ||
        props.showErrorWithoutMessage;

    const inputId = useUniqueId('MultiSelectDropdown');

    const fieldClasses = classnames(
        'usa-select',
        'multiselect-dropdown',
        {
            'usa-input--error': hasError,
            'multiselect-dropdown--open': isOpen,
        },
        props.customCssClassName,
    );

    const formGroupClasses = classnames(
        'usa-form-group',
        {
            'usa-form-group--error': hasError,
            'margin-top-2': props.small,
        },
        props.customCssClassName,
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle option selection
    const handleOptionClick = (optionValue: string | number) => {
        const currentValues = props.value || [];
        let newValues: (string | number)[];

        if (currentValues.includes(optionValue)) {
            // Remove if already selected
            newValues = currentValues.filter(val => val !== optionValue);
        } else {
            // Add if not selected
            newValues = [...currentValues, optionValue];
        }

        // Create a custom event for onChange
        const customEvent: MultiSelectChangeEvent = {
            target: {
                name: props.name || '',
                value: newValues,
            },
        };

        props.onChange?.(customEvent);
    };

    // Handle removing a selected item
    const handleRemoveItem = (valueToRemove: string | number, event: React.MouseEvent) => {
        event.stopPropagation();
        const newValues = (props.value || []).filter(val => val !== valueToRemove);

        const customEvent: MultiSelectChangeEvent = {
            target: {
                name: props.name || '',
                value: newValues,
            },
        };

        props.onChange?.(customEvent);
    };

    // Handle clear all
    const handleClearAll = (event: React.MouseEvent) => {
        event.stopPropagation();
        const customEvent: MultiSelectChangeEvent = {
            target: {
                name: props.name || '',
                value: [],
            },
        };

        props.onChange?.(customEvent);
    };

    // Get label for a value
    const getLabelForValue = (value: string | number): string => {
        const choice = props.choices?.find(choice => choice.value === value);
        return choice ? choice.label.toString() : value.toString();
    };

    // Filter choices based on search term
    const filteredChoices = props.choices?.filter(choice =>
        choice.label.toString().toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    // Get display text for the input field
    const getDisplayText = (): string => {
        const selectedValues = props.value || [];
        if (selectedValues.length === 0) {
            return props.emptyChoiceLabel;
        } else if (selectedValues.length === 1) {
            return getLabelForValue(selectedValues[0]);
        } else {
            return `${selectedValues.length} items selected`;
        }
    };

    return (
        <div className={formGroupClasses} ref={dropdownRef}>
            <FormLabel
                errorMsg={
                    // Show "This field is required" if submitted is true and no value is selected
                    (props.submitted && (!props.value || props.value.length === 0) && 'This field is required') ||
                    props.errorMsg ||
                    (props.showErrorWithoutMessage && ' ')
                }
                hint={props.hint}
                inputId={inputId}
                bold={props.bold}
                optionalText={props.optionalText}
                labelSuffix={props.labelSuffix}
            >
                {props.label}
                {props.mandatoryLabel && <span className="usa-mandatory-label">*</span>}
            </FormLabel>

            <div className={`${styles.multiselectContainer}`}>
                {/* Main input field */}
                <div
                    className={classnames(styles.multiselectDropdown, {
                        [styles.multiselectDropdown + '--error']: hasError,
                        [styles.multiselectDropdown + '--open']: isOpen,})}
                    onClick={() => !props.disabled && setIsOpen(!isOpen)}
                    style={{
                        cursor: props.disabled ? 'not-allowed' : 'pointer',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: props.disabled ? '#f0f0f0' : 'white',
                        position: 'relative'
                    }}
                >
                    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                        {/* Display selected items as tags */}
                        {props.value && props.value.length > 0 ? (
                            props.showSelectedAsTags ? (
                                props.value.map(selectedValue => (
                                    <span
                                        key={selectedValue}
                                        className="styles.multiselectTag"
                                    >
                                        <span style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {getLabelForValue(selectedValue)}
                                        </span>
                                        {!props.disabled && (
                                            <button
                                                type="button"
                                                onClick={(e) => handleRemoveItem(selectedValue, e)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    padding: '0',
                                                    fontSize: '14px',
                                                    lineHeight: '1'
                                                }}
                                                aria-label={`Remove ${getLabelForValue(selectedValue)}`}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </span>
                                ))
                            ) : (
                                <span style={{ color: props.value.length === 0 ? '#757575' : 'inherit' }}>
                                    {getDisplayText()}
                                </span>
                            )
                        ) : (
                            <span style={{ color: '#757575' }}>
                                {props.emptyChoiceLabel}
                            </span>
                        )}
                    </div>

                    {/* Clear all button */}
                    {!props.disabled && props.value && props.value.length > 0 && (
                        <button
                            type="button"
                            onClick={handleClearAll}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#757575',
                                cursor: 'pointer',
                                padding: '4px',
                                marginLeft: '8px',
                                fontSize: '16px'
                            }}
                            aria-label="Clear all selections"
                        >
                            ×
                        </button>
                    )}

                    {/* Dropdown arrow */}
                    <span
                        style={{
                            marginLeft: '8px',
                            color: '#757575',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                        }}
                    >
                        ▼
                    </span>
                </div>

                {/* Dropdown menu */}
                {isOpen && !props.disabled && (
                    <div
                        className="multiselect-dropdown-menu"
                        style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderTop: 'none',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    >
                        {/* Search input */}
                        {props.searchable && (
                            <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Search options..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '4px 8px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        )}

                        {/* Select All / Deselect All options */}
                        {props.allowSelectAll && filteredChoices.length > 0 && (
                            <div style={{ borderBottom: '1px solid #eee' }}>
                                <div
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: '#f8f9fa',
                                        fontWeight: 'bold'
                                    }}
                                    onClick={() => {
                                        const allValues = filteredChoices.map(choice => choice.value);
                                        const currentValues = props.value || [];
                                        const allSelected = allValues.every(val => currentValues.includes(val));

                                        const newValues: (string | number)[] = allSelected
                                            ? currentValues.filter(val => !allValues.includes(val))
                                            : Array.from(new Set<string | number>([...currentValues, ...allValues]));

                                        const customEvent: MultiSelectChangeEvent = {
                                            target: {
                                                name: props.name || '',
                                                value: newValues
                                            },
                                        };

                                        props.onChange?.(customEvent);
                                    }}
                                >
                                    {filteredChoices.every(choice => (props.value || []).includes(choice.value))
                                        ? '✓ Deselect All'
                                        : '☐ Select All'
                                    }
                                </div>
                            </div>
                        )}

                        {/* Options list */}
                        {filteredChoices.length > 0 ? (
                            filteredChoices.map((choice) => {
                                const isSelected = (props.value || []).includes(choice.value);
                                return (
                                    <div
                                        key={choice.value}
                                        className="multiselect-option"
                                        style={{
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#e6f3ff' : 'transparent',
                                            borderLeft: isSelected ? '3px solid #005ea2' : '3px solid transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                        onClick={() => handleOptionClick(choice.value)}
                                        onMouseOver={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = '#f0f0f0';
                                            }
                                        }}
                                        onMouseOut={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }
                                        }}
                                    >
                                        <span style={{
                                            fontSize: '14px',
                                            color: isSelected ? '#005ea2' : '#333'
                                        }}>
                                            {isSelected ? '✓' : '☐'}
                                        </span>
                                        <span style={{
                                            flex: 1,
                                            color: isSelected ? '#005ea2' : '#333',
                                            fontWeight: isSelected ? '500' : 'normal'
                                        }}>
                                            {choice.label}
                                        </span>
                                    </div>
                                );
                            })
                        ) : (
                            <div style={{
                                padding: '16px',
                                textAlign: 'center',
                                color: '#757575',
                                fontSize: '14px'
                            }}>
                                No options found
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Hidden select for form compatibility */}
            <select
                name={props.name}
                value=""
                onChange={() => {}} // Controlled by our custom logic
                style={{ display: 'none' }}
                multiple
                data-item-id={props.itemId}
            >
                {props.choices?.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                        {choice.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

export interface MultiSelectDropdownProps {
    customCssClassName?: string;
    /**
     * If you want to show it as an error without the message
     */
    showErrorWithoutMessage?: boolean | undefined;
    /**
     * List of choices to be rendered in the dropdown
     */
    choices: DropDownItemChoice[] | undefined;
    /**
     * Localized label for the initially selected option when no value is set
     */
    emptyChoiceLabel: string;
    /**
     * Localized error message. Setting this enables the error state styling.
     */
    errorMsg?: React.ReactNode;
    /**
     * Localized hint text
     */
    hint?: React.ReactNode;
    /**
     * Localized label
     */
    label: React.ReactNode;
    /**
     * HTML input `name` attribute
     */
    name: React.InputHTMLAttributes<HTMLInputElement>['name'];
    /**
     * HTML input `onChange` attribute - receives custom event with array of selected values
     */
    onChange?: (event: MultiSelectChangeEvent) => void;
    /**
     * Localized text indicating this field is optional
     */
    optionalText?: React.ReactNode;
    /** Array of selected values */
    value: (string | number)[] | undefined;
    /**
     * Enable the bold variant of label
     */
    bold?: boolean;
    small?: boolean;
    /** event.target.dataset.itemId to determine which item in a series you are updating */
    itemId?: string;
    /** disable the dropdown */
    disabled?: boolean;
    /**
     * Localized text to append after label inline
     */
    labelSuffix?: string;
    mandatoryLabel?: boolean;
    submitted?: boolean;
    /**
     * Enable search functionality within the dropdown
     */
    searchable?: boolean;
    /**
     * Show selected items as removable tags instead of count
     */
    showSelectedAsTags?: boolean;
    /**
     * Allow select all / deselect all functionality
     */
    allowSelectAll?: boolean;
}

/*
 * Use with MultiSelectDropdown component's props.choices. Define list of dropdown options
 */
export interface DropDownItemChoice {
    /**
     * Localized field label
     */
    label: number | string;
    /**
     * Sets the option's value
     */
    value: number | string;
}

export default MultiSelectDropdown;